import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatLegacySelectChange as MatSelectChange } from '@angular/material/legacy-select';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import { tap, takeUntil, finalize } from 'rxjs/operators';

// CONFIGS
import {
  dimensionUnits,
  weightUnits,
} from '../../../invoice/config/shipstation.config';
import { SalesOrderConfig } from '../../../sales-order/configs/sales-order.config';
import { SnackBarConfig } from '../../../shared/configs/snackbar.config';

// INTERFACES
import { OptionInterface } from '../../../shared/interfaces';

// UTILS
import { ShipstationApiService } from '../../../shared/services/api/shipstation-api.service';
import {
  trackByLabel,
  trackByCode,
  trackById,
  trackByValue,
} from '../../../shared/util/track-by.util';

// SERVICES
import {
  IShipStationCarriers,
  IShipstationPackagesServices,
} from '../../../shipping/interfaces/shipstation.interface';
import { IAppIntegrationShippingPreset } from '../../intefaces/app-integration.interface';

import { EWeightUnit } from './../../../invoice/interfaces/shipstation-shipment.interface';

@Component({
  selector: 'lum-app-integration-shipping-options-form',
  templateUrl: './app-integration-shipping-options-form.component.html',
  styleUrls: ['./app-integration-shipping-options-form.component.scss'],
})
export class AppIntegrationShippingOptionsFormComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input()
  selectedRows :any[] = [];

  @Input()
  appIntegrationAccountId :number | null = null;

  @Input()
  commonAppIntegrationAccountId :number | null = null;

  @Input()
  loading :boolean = false;

  @Input()
  hasSameAppIntegrationId :boolean = false;

  @Input()
  actionTitle :string = 'Save';

  @Input()
  hideCancel :boolean = false;

  @Input()
  message :string | null = null;

  @Input()
  currentShippingPreset :any | null = null;

  @Output()
  cancelEvent = new EventEmitter<void>();

  @Output()
  submitEvent = new EventEmitter<any>();

  form :UntypedFormGroup;
  carriers :IShipStationCarriers[] = [];
  services :IShipstationPackagesServices[] = [];
  packages :IShipstationPackagesServices[] = [];
  loadingServices :boolean = false;
  loadingCarriers :boolean = false;
  loadingPackages :boolean = false;

  loadingBulkUpdateShippingLabel :boolean = false;
  weightUnits :OptionInterface[] = weightUnits;
  dimensionUnits :OptionInterface[] = dimensionUnits;

  shippingParams :{ appIntegrationAccountId :string } = {} as {
    appIntegrationAccountId :string;
  };

  shippingOptions = JSON.parse(JSON.stringify(SalesOrderConfig.shippingOptions));

  private onDestroy$ = new Subject();

  trackByLabel = trackByLabel;
  trackByCode = trackByCode;
  trackById = trackById;
  trackByValue = trackByValue;

  constructor(
    private cd :ChangeDetectorRef,
    private fb :UntypedFormBuilder,
    private shipstationApiService :ShipstationApiService,
    private snackBar :MatSnackBar,
  ) {}

  ngOnInit() :void {
    this.initCarriers();
    this.initForm(this.currentShippingPreset);
    this.initShippingOptions();
  }

  ngOnDestroy() :void {
    this.form.reset();
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  ngOnChanges({ selectedRows, currentShippingPreset } :SimpleChanges) :void {
    if (selectedRows?.currentValue && !this.appIntegrationAccountId) {
      this.initCarriers();
    }
    if (currentShippingPreset) {
      if (currentShippingPreset.currentValue) {
        // Get carriers from the current shipping preset
        const params = {
          carrierCode: currentShippingPreset.currentValue.carrierCode,
          appIntegrationAccountId: this.appIntegrationAccountId?.toString() || '',
        };
        this.getCarriers({
          appIntegrationAccountId:
            currentShippingPreset.currentValue.appIntegrationAccountId,
        });
        this.getServices(params);
        this.getPackages(params);
        // Set currentShippingPreset.curernt value property to null if shippingPreset property check is false
        const filteredForm = Object.keys(currentShippingPreset.currentValue)
          .filter(
            (key) => this.shippingOptions[key] && this.shippingOptions[key].checked,
          )
          .reduce((obj :any, key) => {
            obj[key] = currentShippingPreset.currentValue[key];

            return obj;
          }, {});

        this.initForm(filteredForm);
      } else {
        this.shippingOptions = JSON.parse(
          JSON.stringify(SalesOrderConfig.shippingOptions),
        );
      }
    }
  }

  onToggleField(field :any) :void {
    this.shippingOptions[field.name].checked =
      !this.shippingOptions[field.name].checked;
  }

  onSetUnit(field :string, unit :string) :void {
    this.form.get(field)?.patchValue(unit);
  }

  // TODO: Why not access value directly from formControlName?
  onServiceChange(serviceCode :string) :void {
    const service = this.services.find((service) => service.code === serviceCode);

    if (service?.id) {
      this.form.patchValue({
        carrierServiceId: service.carrierServiceId,
      });
    }
  }

  onSelectCarrier(event :MatSelectChange) :void {
    const params = {
      carrierCode: event.value,
      appIntegrationAccountId: this.appIntegrationAccountId?.toString() || '',
    };

    this.getPackages(params);
    this.getServices(params);

    this.form.patchValue({
      packageCode: '',
      serviceCode: '',
    });
  }

  onCancel() :void {
    // Set all fields to unchecked
    this.shippingOptions = JSON.parse(
      JSON.stringify(SalesOrderConfig.shippingOptions),
    );
    this.form.reset();
    this.cancelEvent.emit();
  }

  onSubmit() :void {
    // Filter form.value that is empty or null
    const filteredForm = Object.keys(this.form.value)
      .filter(
        (key) => this.shippingOptions[key] && this.shippingOptions[key].checked,
      )
      .reduce((obj :any, key) => {
        obj[key] = this.form.value[key];

        return obj;
      }, {});

    this.submitEvent.emit({
      ...filteredForm,
      weight: {
        units: this.form.get('weight.valueLbs')?.value
          ? EWeightUnit.POUNDS
          : EWeightUnit.OUNCES,
        value: this.form.get('weight.valueLbs')?.value
          ? this.form.get('weight.valueLbs')?.value
          : this.form.get('weight.valueOz')?.value,
      },
    });
  }

  private initForm(presets :IAppIntegrationShippingPreset | null) :void {
    this.form = this.fb.group({
      carrierCode: [presets?.carrierCode || '', Validators.required],
      weight: this.fb.group({
        units: [presets?.weight?.units || EWeightUnit.POUNDS],
        valueLbs: [
          presets?.weight?.units === EWeightUnit.POUNDS
            ? presets?.weight?.value
            : 0,
          [Validators.required],
        ],
        valueOz: [
          presets?.weight?.units === EWeightUnit.OUNCES
            ? presets?.weight?.value
            : 0,
          [Validators.required],
        ],
      }),
      serviceCode: [presets?.serviceCode || '', Validators.required],
      packageCode: [presets?.packageCode || '', Validators.required],
      dimensions: this.fb.group({
        units: [presets?.dimensions?.units || 'inches'],
        length: [presets?.dimensions?.length || 0],
        width: [presets?.dimensions?.width || 0],
        height: [presets?.dimensions?.height || 0],
      }),
      confirmation: [presets?.confirmation || 'none'],
      shipByDate: [presets?.shipByDate || null, [Validators.required]],
      isDefault: [presets?.isDefault || false, [Validators.required]],
    });
  }

  private initCarriers() :void {
    if (this.appIntegrationAccountId) {
      this.shippingParams.appIntegrationAccountId =
        this.appIntegrationAccountId.toString();
    }

    if (!this.appIntegrationAccountId && this.commonAppIntegrationAccountId) {
      this.shippingParams.appIntegrationAccountId =
        this.commonAppIntegrationAccountId.toString();
    }

    this.getCarriers(this.shippingParams);
  }

  private getCarriers(params :{ [param :string] :string | string[] }) :void {
    this.loadingCarriers = true;
    this.shipstationApiService
      .getCarriersV2(params)
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => {
          this.loadingCarriers = false;
          this.cd.detectChanges();
        }),
      )
      .subscribe({
        next: (response :any) => {
          const { data } = response;
          if (data) {
            this.carriers = data;
          }
        },
        error: (err) => {
          this.snackBar.open(err.error, 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  private getServices(params :{ [param :string] :string | string[] }) :void {
    this.loadingServices = true;
    this.shipstationApiService
      .getServicesV2(params)
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => {
          this.loadingServices = false;
          this.cd.detectChanges();
        }),
      )
      .subscribe({
        next: (response :any) => {
          const { data } = response;
          if (data) {
            this.services = data;
          }
        },
        error: (err) => {
          this.snackBar.open(err.error, 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  private getPackages(params :{ [param :string] :string | string[] }) :void {
    this.loadingPackages = true;
    this.shipstationApiService
      .getPackagesV2(params)
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => {
          this.loadingPackages = false;
          this.cd.detectChanges();
        }),
      )
      .subscribe({
        next: (response :any) => {
          const { data } = response;
          if (data) {
            this.packages = data;
          }
        },
        error: (err) => {
          this.snackBar.open(err.error, 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  private initShippingOptions() :void {
    if (this.currentShippingPreset) {
      const updatedShippingOptions = JSON.parse(
        JSON.stringify(SalesOrderConfig.shippingOptions),
      );

      Object.keys(this.shippingOptions).forEach((key) => {
        if (this.currentShippingPreset && this.currentShippingPreset[key]) {
          switch (key) {
            case 'carrierCode':
              updatedShippingOptions[key].checked = true;
              const params = {
                carrierCode: this.currentShippingPreset[key],
                appIntegrationAccountId:
                  this.appIntegrationAccountId?.toString() || '',
              };

              this.getPackages(params);
              this.getServices(params);

              break;

            case 'weight':
              if (this.currentShippingPreset[key].value !== 0) {
                updatedShippingOptions[key].checked = true;
              }
              break;

            case 'dimensions':
              if (
                this.currentShippingPreset[key].length !== 0 &&
                this.currentShippingPreset[key].width !== 0 &&
                this.currentShippingPreset[key].height !== 0
              ) {
                updatedShippingOptions[key].checked = true;
              }
              break;

            default:
              if (updatedShippingOptions[key]) {
                updatedShippingOptions[key].checked = true;
              }

              break;
          }

          this.shippingOptions = updatedShippingOptions;
        }
      });
    }
  }

  get fieldsArray() :any[] {
    return Object.values(this.shippingOptions);
  }

  get weightUnit() :string {
    const unit = this.form.get('weight.units')?.value;
    const weightUnit = this.weightUnits.find((obj) => obj.value === unit);

    return weightUnit?.label || '';
  }

  get dimensionUnit() :string {
    const unit = this.form.get('dimensions.units')?.value;
    const dimensionUnit = this.dimensionUnits.find((obj) => obj.value === unit);

    return dimensionUnit?.label || '';
  }

  get confirmationOptions() :string[] {
    let options = ['none', 'delivery', 'signature', 'adult_signature'];

    if (this.form.get('carrierCode')?.value === 'fedex') {
      options.push('direct_signature');
    } else {
      if (options.includes('direct_signature')) {
        options = options.filter((option) => option !== 'direct_signature');
      }
    }

    return options;
  }

  get hasSelectedInputFields() :boolean {
    const filteredForm = Object.keys(this.form.value)
      .filter(
        (key) => this.shippingOptions[key] && this.shippingOptions[key].checked,
      )
      .reduce((obj :any, key) => {
        obj[key] = this.form.value[key];

        return obj;
      }, {});

    return Object.keys(filteredForm).length > 0;
  }

  get shipDateControl() :UntypedFormControl {
    return this.form.get('shipDate') as UntypedFormControl;
  }

  get carrierCodeControl() :UntypedFormControl {
    return this.form.get('carrierCode') as UntypedFormControl;
  }

  get weightValueControl() :UntypedFormControl {
    return this.form.get('weightValue') as UntypedFormControl;
  }
}
